import React, { useCallback, useEffect, useState } from "react";
import { FormHeader } from "@components/Header";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  FormBody,
  FormContentContainer,
  BottomActionButton,
  BottomActionArea,
  FullScreenLoader,
} from "@components";
import { Box, styled, Typography } from "@mui/material";
import { BankTransactionStatus } from "@interfaces";
import { CheckCircleOutlined } from "@mui/icons-material";
import { useBankTransferAttempts, useVerifyBankTransferStatus } from "@hooks";
import { tracker } from "@utils/openreplay";
import { ENROLMENT_STEP } from "@mekidoc/types";
import { useEnrolmentStatusContext } from "@contexts/EnrolmentStatusContext";
import { BankTransferFailureScreen } from "./BankTransferFailureScreen";

const SOURCE = "SuccessfulBankTransferScreen";

const StyledCheckCircleIcon = styled(CheckCircleOutlined)(({ theme }) => ({
  fontSize: "46px",
  color: theme.palette.success.main,
}));

export const SuccessfulBankTransferScreen: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const { getEnrolmentStatus } = useEnrolmentStatusContext();
  const { resetAttempts } = useBankTransferAttempts();
  const { data: transferStatusData, error: errorGettingTransactionStatus } =
    useVerifyBankTransferStatus();

  const bankTransferStatus = transferStatusData?.bankTransferStatus;
  const isApproved = bankTransferStatus === BankTransactionStatus.APPROVED;
  const hasFailed =
    Boolean(errorGettingTransactionStatus) ||
    bankTransferStatus === BankTransactionStatus.REJECTED;

  const onClickNext = useCallback(async () => {
    const requestId = searchParams.get("requestId");
    const queryParams = requestId ? `?requestId=${requestId}` : "";
    const enrolmentStatus = await getEnrolmentStatus();
    if (enrolmentStatus?.step !== ENROLMENT_STEP.CERTIFICATE) return;
    navigate(
      `/app/registro/firma-electronica-avanzada/crear-pin${queryParams}`,
    );
  }, [navigate, searchParams, getEnrolmentStatus]);

  const handleRetry = useCallback(async () => {
    setIsLoading(true);
    await getEnrolmentStatus();
  }, [getEnrolmentStatus]);

  useEffect(() => {
    if (!isApproved) return;
    tracker?.event("BankTransferChallengeSuccess", { source: SOURCE });
    resetAttempts();
  }, [isApproved, resetAttempts]);

  useEffect(() => {
    if (errorGettingTransactionStatus) {
      tracker?.event("BankTransferChallengeStatusError", {
        source: SOURCE,
        error: errorGettingTransactionStatus,
        errorMessage: errorGettingTransactionStatus?.message,
      });
    }
  }, [errorGettingTransactionStatus]);

  if (hasFailed)
    return (
      <BankTransferFailureScreen
        source={SOURCE}
        onRetry={handleRetry}
        isRetrying={isLoading}
      />
    );

  if (!isApproved)
    return (
      <>
        <FormHeader useMekiDocIcon />
        <FormContentContainer>
          <FormBody justifyContent="center">
            <FullScreenLoader title="Validando transferencia bancaria" />
          </FormBody>
        </FormContentContainer>
      </>
    );

  return (
    <>
      <FormHeader useMekiDocIcon />
      <FormContentContainer>
        <FormBody justifyContent="center">
          <Box
            marginBottom="48px"
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap="16px"
          >
            <StyledCheckCircleIcon />
            <Typography variant="h5" fontWeight="semibold">
              Transferencia exitosa
            </Typography>
          </Box>
          <BottomActionArea>
            <BottomActionButton onClick={onClickNext}>
              Continuar
            </BottomActionButton>
          </BottomActionArea>
        </FormBody>
      </FormContentContainer>
    </>
  );
};
